import { IonButton, IonButtons, IonCheckbox, IonCol, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonModal, IonRadio, IonRadioGroup, IonRow, IonSearchbar, IonText, IonTitle, IonToolbar } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { Auditor, EventAuditor, OrganizationAuditor, Person } from "../../models";
import { getSegmentOfAuditors } from "../../utilities/auditor/Auditor";
import ErrorAlert from "../Errors/ErrorAlert";
import Spinner from "../Spinners/Spinner";
import { Table } from "reactstrap";
import PhoneInput from "react-phone-number-input";
import { getEventAuditorsByPhoneNumberSearchWithFilters } from "../../utilities/eventAuditor/EventAuditor";
import { mergeAuditors } from "../../utilities/auditor/MergeAuditors";
import { deleteAuditorWithRelatedRecords } from "../../utilities/auditor/DeleteAuditor";
import { close, trash } from "ionicons/icons";
import { getOrganizationAuditorsByPhoneNumberSearch } from "../../utilities/organizationAuditor/OrganizationAuditor";
import { generateAllTrainerPhoneNumbersReport } from "../../utilities/reports/AllAuditorsReport";

var moment = require("moment");

export interface FormattedAuditor {
    id: string
    auditor: Auditor
    eventAuditors: EventAuditor[]
    organizationAuditors: OrganizationAuditor[]
}

const AuditorListEditor: React.FC = () => {

    const [auditors, setAuditors] = useState<Auditor[] | null | undefined>();
    const [searchText, setSearchText] = useState("");
    const [formattedAuditors, setFormattedAuditors] = useState<FormattedAuditor[] | null | undefined>();
    const [filteredAuditors, setFilteredAuditors] = useState<FormattedAuditor[] | null | undefined>();
    const [duplicatePhoneNumbers, setDuplicatePhoneNumbers] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    
    // Pagination state
    const [storedNextToken, setStoredNextToken] = useState<string | undefined | null>(undefined);
    const [isCompleteList, setIsCompleteList] = useState(false);
    
    // Merge functionality state
    const [selectedAuditorIds, setSelectedAuditorIds] = useState<Set<string>>(new Set());
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [primaryAuditorId, setPrimaryAuditorId] = useState<string>("");
    const [isMerging, setIsMerging] = useState(false);
    const [mergeError, setMergeError] = useState("");
    
    // Delete functionality state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [auditorToDelete, setAuditorToDelete] = useState<Auditor | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    const [progressMessage, setProgressMessage] = useState("");

    // Calculate which phone numbers appear more than once
    const calculateDuplicatePhoneNumbers = (auditorList: Auditor[]): Set<string> => {
        const phoneNumberCounts = new Map<string, number>();
        const duplicates = new Set<string>();
        
        auditorList.forEach((auditor) => {
            if (auditor.phoneNumber) {
                const count = phoneNumberCounts.get(auditor.phoneNumber) || 0;
                phoneNumberCounts.set(auditor.phoneNumber, count + 1);
            }
        });
        
        phoneNumberCounts.forEach((count, phoneNumber) => {
            if (count > 1) {
                duplicates.add(phoneNumber);
            }
        });
        
        return duplicates;
    };

    async function getAuditors(numberToLoad: number = 10, appendToExisting: boolean = false) {
        setIsLoading(true);
        setError("");
        setProgressMessage("Getting auditors...");
        
        console.time('auditorsResult');
        const auditorsResult = await getSegmentOfAuditors(numberToLoad, storedNextToken || undefined);
        console.timeEnd('auditorsResult');

        console.log("auditorsResult: ", auditorsResult);
        
        if (auditorsResult.isSuccess) {
            const results = auditorsResult.result;
            const newAuditors: Auditor[] = results.items;
            const nextToken = results.nextToken;
            
            // Handle pagination token
            if (storedNextToken) {
                if (nextToken) {
                    // Replace the old next token with the new next token
                    setStoredNextToken(nextToken);
                } else {
                    // Hit the end of the list
                    setStoredNextToken(null);
                    setIsCompleteList(true);
                }
            } else {
                // First time storing the next token, store it in state
                setStoredNextToken(nextToken);
                if (!nextToken) {
                    setIsCompleteList(true);
                }
            }
            
            // Combine with existing auditors if appending
            const allAuditors: Auditor[] = appendToExisting && auditors 
                ? [...auditors, ...newAuditors] 
                : newAuditors;
            
            const sorted = allAuditors.sort((a, b) => (a.phoneNumber || "")?.localeCompare((b.phoneNumber || "")));
            setAuditors(sorted);

            setProgressMessage("Finding duplicates...");
            console.time('duplicates');
            // Calculate duplicates on all loaded auditors
            const duplicates = calculateDuplicatePhoneNumbers(sorted);
            setDuplicatePhoneNumbers(duplicates);
            console.timeEnd('duplicates');
            
            // Format ONLY the newly pulled auditors
            console.time('formatAuditors');
            const newFormattedAuditors = await formatNewAuditors(newAuditors);
            console.timeEnd('formatAuditors');
            
            // Append newly formatted auditors to existing formatted auditors
            const allFormattedAuditors = appendToExisting && formattedAuditors
                ? [...formattedAuditors, ...newFormattedAuditors]
                : newFormattedAuditors;

            const allSortedFormattedAuditors = allFormattedAuditors.sort((a, b) => (a.auditor.phoneNumber || "")?.localeCompare((b.auditor.phoneNumber || "")));
            
            setFormattedAuditors(allSortedFormattedAuditors);
            setFilteredAuditors(allSortedFormattedAuditors);
            
            setError("");
        } else {
            setError("Could not get the auditors from the database.");
        }
        setIsLoading(false);
    }

    // Format only new auditors without touching existing formatted auditors
    async function formatNewAuditors(newAuditors: Auditor[]): Promise<FormattedAuditor[]> {
        const newFormattedAuditorList: FormattedAuditor[] = [];
        
        try {
            for (let i = 0; i < newAuditors.length; i++) {
                const auditor = newAuditors[i];

                const progress = (((i+1)/newAuditors.length)*100).toFixed(0);
                const progressMsg = "Formatted " + progress.toString() + "% - aka " + (i + 1).toString() + " out of " + (newAuditors.length).toString();
                setProgressMessage(progressMsg);

                let eventAuditorList: EventAuditor[] = [];
                if (auditor.phoneNumber) {
                    const eventAuditorQuery = await getEventAuditorsByPhoneNumberSearchWithFilters(auditor.phoneNumber);
                    if (eventAuditorQuery.isSuccess) {
                        eventAuditorList = eventAuditorQuery.result;
                    }
                }

                let orgAuditorList: OrganizationAuditor[] = [];
                if (auditor.phoneNumber) {
                    const orgAuditorQuery = await getOrganizationAuditorsByPhoneNumberSearch(auditor.phoneNumber);
                    if (orgAuditorQuery.isSuccess) {
                        orgAuditorList = orgAuditorQuery.result;
                    }
                }

                const formatted: FormattedAuditor = {
                    id: auditor.id,
                    auditor: auditor,
                    eventAuditors: eventAuditorList,
                    organizationAuditors: orgAuditorList
                };

                console.log("formatted: ", formatted);
                newFormattedAuditorList.push(formatted);
            }
        } catch (error) {
            console.log("ERROR formatting auditors: ", error);
        }
        
        return newFormattedAuditorList;
    }

    useEffect(() => {
        getAuditors();
    }, []);

    const handleSearchInput = (input: string) => {
        // If search input is empty, reset to all accepted entries
        if (!input || input === "") {
            setFilteredAuditors(formattedAuditors);
        } else {
            // Try search by entry number
            let result = formattedAuditors?.filter((formattedAuditor: FormattedAuditor) => {
                const found = (formattedAuditor?.auditor?.name?.toLowerCase().includes(input.toLowerCase()) ||
                formattedAuditor?.auditor?.phoneNumber?.includes(input.toLowerCase()));
                return found;
            });
            setFilteredAuditors(result);
        }
    }

    const handleSelectedAuditor = (id: string) => {
        console.log("Clicked on an Auditor: ", id);
    }

    // Open delete modal
    const handleOpenDeleteModal = (auditor: Auditor) => {
        setAuditorToDelete(auditor);
        setShowDeleteModal(true);
        setDeleteError("");
    }

    // Cancel delete
    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setAuditorToDelete(null);
        setDeleteError("");
    }

    // Confirm and execute delete
    const handleConfirmDelete = async () => {
        if (!auditorToDelete) {
            setDeleteError("No auditor selected for deletion");
            return;
        }

        setIsDeleting(true);
        setDeleteError("");

        try {
            const deleteResult = await deleteAuditorWithRelatedRecords(auditorToDelete);

            if (deleteResult.isSuccess) {
                // Refresh the auditor list
                await getAuditors();
                
                // Close modal and clear state
                setShowDeleteModal(false);
                setAuditorToDelete(null);
                setError("");
            } else {
                setDeleteError(deleteResult.message || "Failed to delete auditor");
            }
        } catch (error: any) {
            setDeleteError("An error occurred while deleting the auditor");
            console.error("Delete error:", error);
        }

        setIsDeleting(false);
    }

    // Toggle selection of an auditor for merging
    const handleToggleSelection = (auditorId: string, phoneNumber: string | null | undefined) => {
        const newSelected = new Set(selectedAuditorIds);
        
        if (newSelected.has(auditorId)) {
            newSelected.delete(auditorId);
        } else {
            // Only allow selection of auditors with the same phone number
            if (phoneNumber && duplicatePhoneNumbers.has(phoneNumber)) {
                // Check if we already have selections
                if (newSelected.size > 0) {
                    // Get the phone number of already selected auditors
                    const firstSelectedId = Array.from(newSelected)[0];
                    const firstSelectedAuditor = auditors?.find(a => a.id === firstSelectedId);
                    
                    // Only allow if same phone number
                    if (firstSelectedAuditor?.phoneNumber === phoneNumber) {
                        newSelected.add(auditorId);
                    } else {
                        setError("Can only merge auditors with the same phone number");
                        return;
                    }
                } else {
                    newSelected.add(auditorId);
                }
            } else {
                setError("Can only select duplicate auditors (same phone number)");
                return;
            }
        }
        
        setSelectedAuditorIds(newSelected);
        setError(""); // Clear any previous errors
    }

    // Check if all selected auditors share the same phone number
    const selectedAuditorsSharePhoneNumber = (): boolean => {
        if (selectedAuditorIds.size < 2) return false;
        
        const selectedAuditors = auditors?.filter(a => selectedAuditorIds.has(a.id));
        if (!selectedAuditors || selectedAuditors.length < 2) return false;
        
        const firstPhoneNumber = selectedAuditors[0].phoneNumber;
        return selectedAuditors.every(a => a.phoneNumber === firstPhoneNumber);
    }

    // Open merge modal
    const handleOpenMergeModal = () => {
        if (selectedAuditorIds.size < 2) {
            setError("Please select at least 2 auditors to merge");
            return;
        }
        
        if (!selectedAuditorsSharePhoneNumber()) {
            setError("Selected auditors must all have the same phone number");
            return;
        }
        
        // Set the first selected auditor as default primary
        setPrimaryAuditorId(Array.from(selectedAuditorIds)[0]);
        setShowMergeModal(true);
        setMergeError("");
    }

    // Cancel merge
    const handleCancelMerge = () => {
        setShowMergeModal(false);
        setPrimaryAuditorId("");
        setMergeError("");
    }

    // Confirm and execute merge
    const handleConfirmMerge = async () => {
        if (!primaryAuditorId) {
            setMergeError("Please select a primary auditor");
            return;
        }

        setIsMerging(true);
        setMergeError("");

        try {
            const primaryAuditor = auditors?.find(a => a.id === primaryAuditorId);
            const duplicateAuditors = auditors?.filter(a => 
                selectedAuditorIds.has(a.id) && a.id !== primaryAuditorId
            );

            if (!primaryAuditor || !duplicateAuditors || duplicateAuditors.length === 0) {
                setMergeError("Invalid selection for merge");
                setIsMerging(false);
                return;
            }

            const mergeResult = await mergeAuditors({
                primaryAuditor,
                duplicateAuditors
            });

            if (mergeResult.isSuccess) {
                // Refresh the auditor list
                await getAuditors();
                
                // Clear selections and close modal
                setSelectedAuditorIds(new Set());
                setShowMergeModal(false);
                setPrimaryAuditorId("");
                setError("");
            } else {
                setMergeError(mergeResult.message || "Failed to merge auditors");
            }
        } catch (error: any) {
            setMergeError("An error occurred while merging auditors");
            console.error("Merge error:", error);
        }

        setIsMerging(false);
    }

    const handleGenerateAllTrainerPhoneNumbersReport = async () => {
        await generateAllTrainerPhoneNumbersReport(formattedAuditors);
    }

    return (
        <>
           {error && <ErrorAlert width="12" error={error} />}
           <IonRow>
                <IonCol sizeXs="12" sizeMd="8">
                    <IonSearchbar
                        color="white"  
                        placeholder="Search by Name or Phone Number" 
                        value={searchText} 
                        onIonChange={e => {
                            setSearchText(e.detail.value!)
                            handleSearchInput(e.detail.value!)
                        }}
                    />
                </IonCol>
                <IonCol sizeXs="12" sizeMd="4" className="ion-text-right">
                    {selectedAuditorIds.size > 0 && (
                        <>
                            <IonButton 
                                color="light" 
                                onClick={() => setSelectedAuditorIds(new Set())}
                                className="ion-margin-end"
                            >
                                Clear ({selectedAuditorIds.size})
                            </IonButton>
                            <IonButton 
                                color="primary" 
                                onClick={handleOpenMergeModal}
                                disabled={selectedAuditorIds.size < 2}
                            >
                                Merge Selected ({selectedAuditorIds.size})
                            </IonButton>
                        </>
                    )}
                </IonCol>
            </IonRow> 
            {isLoading ?
                <IonRow className="ion-justify-content-center">
                    <IonCol className="ion-text-center">
                        <Spinner />
                        <p>{progressMessage}</p>
                    </IonCol>
                </IonRow>
                :
                <>
                    <IonRow className="ion-padding">
                        <IonCol size="12" sizeMd="6">
                            <p>Total number of auditors loaded: {auditors?.length || 0}</p>
                            {!isCompleteList && (
                                <p className="text-muted">
                                    <small>More records available. Click "Load More" to see additional auditors.</small>
                                </p>
                            )}
                            {isCompleteList && auditors && auditors.length > 0 && (
                                <p className="text-success">
                                    <small>✓ All auditors loaded</small>
                                </p>
                            )}
                            {duplicatePhoneNumbers.size > 0 && (
                                <div className="alert alert-warning">
                                    <strong>⚠ Warning:</strong> Found {duplicatePhoneNumbers.size} duplicate phone number(s) 
                                    affecting {auditors?.filter(a => a.phoneNumber && duplicatePhoneNumbers.has(a.phoneNumber)).length} auditor records.
                                </div>
                            )}
                        </IonCol>
                        <IonCol size="12" sizeMd="6" className="ion-text-right">
                            {!isCompleteList && (
                                <IonButton 
                                    color="primary" 
                                    onClick={() => getAuditors(10, true)}
                                    disabled={isLoading}
                                >
                                    Load More (100)
                                </IonButton>
                            )}
                        </IonCol>
                        <IonCol size="12" sizeMd="6" className="ion-text-right">
                            {!isCompleteList && (
                                <IonButton 
                                    color="primary" 
                                    onClick={handleGenerateAllTrainerPhoneNumbersReport}
                                    disabled={isLoading}
                                >
                                    Generate All Trainer Phone Numbers Report
                                </IonButton>
                            )}
                        </IonCol>
                    </IonRow>
                    {(filteredAuditors && filteredAuditors.length > 0) ?
                        <>
                            <Table hover responsive>
                                <thead>
                                    <tr>
                                        <th>Select</th>
                                        <th>Name</th>
                                        <th>Phone Number</th>
                                        <th>Status</th>
                                        <th>Note</th>
                                        <th>Date Created</th>
                                        <th>Date Updated</th>
                                        <th>Id</th>
                                        <th>Created By</th>
                                        <th>Person Id</th>
                                        <th>Person</th>
                                        <th>Event Auditors</th>
                                        <th>Org Auditors</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAuditors.map((formattedAuditor, index) => {
                                        const isDuplicate = formattedAuditor.auditor.phoneNumber && 
                                                           duplicatePhoneNumbers.has(formattedAuditor.auditor.phoneNumber);
                                        const duplicateCount = isDuplicate 
                                            ? auditors?.filter(a => a.phoneNumber === formattedAuditor.auditor.phoneNumber).length 
                                            : 0;
                                        
                                        return (
                                            <tr 
                                                key={index}
                                                className={isDuplicate ? "bg-warning" : ""}
                                            >
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    {isDuplicate && (
                                                        <IonCheckbox
                                                            checked={selectedAuditorIds.has(formattedAuditor.id)}
                                                            onIonChange={() => handleToggleSelection(
                                                                formattedAuditor.id, 
                                                                formattedAuditor.auditor.phoneNumber
                                                            )}
                                                        />
                                                    )}
                                                </td>
                                                <td onClick={() => handleSelectedAuditor(formattedAuditor.id)}>
                                                    <p className="ion-text-wrap">
                                                        {formattedAuditor.auditor.name || ""}
                                                        {isDuplicate && (
                                                            <span className="text-danger font-weight-bold"> ⚠ DUPLICATE</span>
                                                        )}
                                                    </p>
                                                </td>
                                                <td className="ion-text-wrap">
                                                    {formattedAuditor.auditor.phoneNumber ? (
                                                        <>
                                                            <PhoneInput
                                                                placeholder="No phone number found"
                                                                defaultCountry="US"
                                                                disabled={true}
                                                                value={formattedAuditor.auditor.phoneNumber as any}
                                                                onChange={(phoneNumberValue?: any) => {
                                                                    setError("");
                                                                }}
                                                            />
                                                            {isDuplicate && (
                                                                <small className="text-danger d-block">
                                                                    ⚠ This phone number appears {duplicateCount} times
                                                                </small>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <p></p>
                                                    )}
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{formattedAuditor.auditor.status || ""}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{formattedAuditor.auditor.statusNote || ""}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{moment(formattedAuditor.auditor.createdOn).format("MMM Do YYYY hh:mm a")}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{moment(formattedAuditor.auditor.updatedOn).format("MMM Do YYYY hh:mm a")}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{formattedAuditor.auditor.id}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{formattedAuditor.auditor.createdBy}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{formattedAuditor.auditor.personId}</p>
                                                </td>
                                                <td>
                                                    <p className="ion-text-wrap">{(formattedAuditor.auditor?.person?.firstName ? formattedAuditor.auditor?.person?.firstName : "") + " " + (formattedAuditor.auditor?.person?.lastName ? formattedAuditor.auditor?.person?.lastName : "")}</p>
                                                </td>
                                                {(formattedAuditor.eventAuditors && formattedAuditor.eventAuditors.length > 0) ? (
                                                    <td>
                                                        {formattedAuditor.eventAuditors.map((fa, index) => {
                                                            const filtersObject = fa.filters as any;
                                                            const filtersItems = filtersObject?.items ?? [];
                                                            let filtersText = "";

                                                            if (filtersItems.length > 0) {
                                                                for (let i = 0; i < filtersItems.length; i++) {
                                                                    const currentFilterItem = filtersItems[i];
                                                                    if (currentFilterItem?.filter?.name === "Roles") {
                                                                        filtersText = currentFilterItem?.options?.join(", ") || "";
                                                                    }
                                                                    
                                                                }
                                                            }
                                                        
                                                                return (
                                                                    <div key={index} className="large-input-width">
                                                                        <p className="ion-text-wrap">
                                                                            {fa.event?.name} 
                                                                        </p>
                                                                        <p>
                                                                            {fa.event?.startDate}
                                                                        </p>
                                                                        <p>
                                                                            - <span className="font-weight-bold">{fa.status}</span> 
                                                                        </p>
                                                                        <p>
                                                                            - roles:
                                                                            {filtersText}
                                                                        </p>
                                                                        <hr />
                                                                    </div>
                                                                );
                                                        })}
                                                    </td>
                                                ) :
                                                    <td>
                                                        <p className="ion-text-wrap">none</p>
                                                    </td>
                                                }
                                                {formattedAuditor.organizationAuditors.length > 0 ? (
                                                    <td>
                                                        {formattedAuditor.organizationAuditors.map((fa, index) => (
                                                            <div key={index} className="large-input-width">
                                                                <p className="ion-text-wrap">{fa.organization?.name} - <span className="font-weight-bold">{fa.status}</span></p>
                                                                <hr />
                                                            </div>
                                                        ))}
                                                    </td>
                                                ) :
                                                    <td>
                                                        <p className="ion-text-wrap">none</p>
                                                    </td>
                                                }
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <IonButton
                                                        size="small"
                                                        color="danger"
                                                        onClick={() => handleOpenDeleteModal(formattedAuditor.auditor)}
                                                    >
                                                        <IonIcon icon={trash} />
                                                    </IonButton>
                                                </td>
                                             </tr>
                                         )
                                     })}
                                </tbody>
                            </Table>
                        </>
                        :
                        <>
                            <p>No auditors found.</p>
                        </>
                    }
                </>
            }

            {/* Merge Confirmation Modal */}
            <IonModal backdropDismiss={false} isOpen={showMergeModal}>
                <IonHeader>
                    <IonToolbar color="light">
                        <IonTitle className="ml-0 ion-text-center font-weight-bold">
                            Merge Duplicate Auditors
                        </IonTitle>
                        <IonButtons slot="end">
                            <IonButton fill="clear" onClick={handleCancelMerge} disabled={isMerging}>
                                <IonIcon icon={close} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                {mergeError && <ErrorAlert width="12" error={mergeError} />}

                <IonContent className="ion-padding">
                    <IonRow>
                        <IonCol>
                            <IonText>
                                <p className="text-center">
                                    You are about to merge <strong>{selectedAuditorIds.size} auditor records</strong> into one.
                                </p>
                                <p className="text-danger text-center font-weight-bold">
                                    ⚠ This action cannot be undone!
                                </p>
                            </IonText>
                        </IonCol>
                    </IonRow>

                    <IonRow className="ion-margin-top">
                        <IonCol>
                            <IonText color="primary">
                                <h6 className="font-weight-bold">Select Primary Record:</h6>
                            </IonText>
                            <p className="text-muted">
                                The primary record will be kept. All other selected records will be deleted, 
                                but their EventAuditor and OrganizationAuditor connections will be transferred 
                                to the primary record.
                            </p>
                        </IonCol>
                    </IonRow>

                    <IonRadioGroup value={primaryAuditorId} onIonChange={e => setPrimaryAuditorId(e.detail.value)}>
                        <IonList>
                            {Array.from(selectedAuditorIds).map((auditorId) => {
                                const auditor = auditors?.find(a => a.id === auditorId);
                                if (!auditor) return null;

                                const formattedAuditor = formattedAuditors?.find(fa => fa.id === auditorId);
                                const eventAuditorCount = formattedAuditor?.eventAuditors?.length || 0;

                                return (
                                    <IonItem key={auditorId}>
                                        <IonRadio slot="start" value={auditorId} />
                                        <IonLabel>
                                            <h3>{auditor.name || "Unnamed"}</h3>
                                            <p>Id: {auditor.id}</p>
                                            <p>Phone: {auditor.phoneNumber}</p>
                                            <p>Status: {auditor.status || "N/A"}</p>
                                            {auditor.email && <p>Email: {auditor.email}</p>}
                                            {auditor.personId && <p>Person ID: {auditor.personId}</p>}
                                            <p className="text-info">
                                                Connected to {eventAuditorCount} event(s)
                                            </p>
                                            <small className="text-muted">
                                                Created: {moment(auditor.createdOn).format("MMM DD, YYYY hh:mm a")}
                                            </small>
                                        </IonLabel>
                                    </IonItem>
                                );
                            })}
                        </IonList>
                    </IonRadioGroup>

                    <IonRow className="ion-margin-top">
                        <IonCol>
                            <IonText>
                                <h6 className="font-weight-bold">Merge Details:</h6>
                                <ul>
                                    <li>Non-primary records will be <strong>deleted</strong></li>
                                    <li>All EventAuditor connections will be transferred to primary</li>
                                    <li>All OrganizationAuditor connections will be transferred to primary</li>
                                    <li>Missing data from non-primary records will be copied to primary (name, email, personId, status, statusNote, createdBy)</li>
                                </ul>
                            </IonText>
                        </IonCol>
                    </IonRow>
                </IonContent>

                {isMerging && (
                    <IonRow className="ion-justify-content-center">
                        <IonCol size="12" className="ion-text-center">
                            <Spinner />
                        </IonCol>
                    </IonRow>
                )}

                <IonRow>
                    <IonCol size="6">
                        <IonButton 
                            expand="block" 
                            color="light" 
                            onClick={handleCancelMerge} 
                            disabled={isMerging}
                        >
                            Cancel
                        </IonButton>
                    </IonCol>
                    <IonCol size="6">
                        <IonButton 
                            expand="block" 
                            color="danger" 
                            onClick={handleConfirmMerge} 
                            disabled={isMerging || !primaryAuditorId}
                        >
                            {isMerging ? "Merging..." : "Merge"}
                        </IonButton>
                    </IonCol>
                </IonRow>
            </IonModal>

            {/* Delete Confirmation Modal */}
            <IonModal backdropDismiss={false} isOpen={showDeleteModal}>
                <IonHeader>
                    <IonToolbar color="light">
                        <IonTitle className="ml-0 ion-text-center font-weight-bold">
                            Delete Auditor
                        </IonTitle>
                        <IonButtons slot="end">
                            <IonButton fill="clear" onClick={handleCancelDelete} disabled={isDeleting}>
                                <IonIcon icon={close} />
                            </IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                {deleteError && <ErrorAlert width="12" error={deleteError} />}

                <IonContent className="ion-padding">
                    <IonRow className="ion-justify-content-center">
                        <IonCol className="ion-text-center">
                            <IonText color="primary">
                                <h4 className="font-weight-bold">
                                    Are you sure you want to delete this auditor?
                                </h4>
                            </IonText>
                        </IonCol>
                    </IonRow>

                    {auditorToDelete && (
                        <>
                            <IonRow className="ion-justify-content-center ion-margin-top">
                                <IonCol className="ion-text-center">
                                    <h4 className="border-bottom border-info ion-padding-bottom">
                                        {auditorToDelete.name || "Unnamed Auditor"}
                                    </h4>
                                    <p><strong>Phone:</strong> {auditorToDelete.phoneNumber}</p>
                                    {auditorToDelete.email && <p><strong>Email:</strong> {auditorToDelete.email}</p>}
                                    <p><strong>Status:</strong> {auditorToDelete.status || "N/A"}</p>
                                    {auditorToDelete.personId && <p><strong>Person ID:</strong> {auditorToDelete.personId}</p>}
                                    <p className="text-muted">
                                        <small>ID: {auditorToDelete.id}</small>
                                    </p>
                                </IonCol>
                            </IonRow>

                            <IonRow className="ion-justify-content-center ion-margin-top">
                                <IonCol className="ion-text-center">
                                    <IonText color="danger">
                                        <h5 className="font-weight-bold">⚠ This action cannot be undone!</h5>
                                        <p>This will also delete:</p>
                                        <ul className="text-left">
                                            <li>All EventAuditor records connected to this auditor</li>
                                            <li>All OrganizationAuditor records connected to this auditor</li>
                                        </ul>
                                    </IonText>
                                </IonCol>
                            </IonRow>
                        </>
                    )}
                </IonContent>

                {isDeleting && (
                    <IonRow className="ion-justify-content-center">
                        <IonCol size="12" className="ion-text-center">
                            <Spinner />
                        </IonCol>
                    </IonRow>
                )}

                <IonRow>
                    <IonCol size="6">
                        <IonButton 
                            expand="block" 
                            color="light" 
                            onClick={handleCancelDelete} 
                            disabled={isDeleting}
                        >
                            Cancel
                        </IonButton>
                    </IonCol>
                    <IonCol size="6">
                        <IonButton 
                            expand="block" 
                            color="danger" 
                            onClick={handleConfirmDelete} 
                            disabled={isDeleting}
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </IonButton>
                    </IonCol>
                </IonRow>
            </IonModal>
        </>
    );
};

export default AuditorListEditor;
